import { getDateFormat } from 'helpers/dates'
import React from 'react'
import T from 'prop-types'
import { connect } from 'react-redux'
import cn from 'classnames'
import { injectIntl } from 'react-intl'
import { FiberManualRecord } from '@material-ui/icons'
import moment from 'moment'
import {
  userFetchTableData,
  sendInvitation,
  userClearStateList,
  userDelete,
  setPassword,
} from 'reducers/user/reducer'
import ListFromTable from 'components/common/ListFromTable'
import ColumnCollection from 'types/table/ColumnCollection'
import Column from 'types/table/Column'
import ActionCollection from 'types/table/ActionCollection'
import Action from 'types/table/Action'
import { USER_CREATE, USER_EDIT, USER_LIST } from 'constants/appRouting'
import UrlHelper from 'helpers/url'
import Sort from 'lib/psfs/Sort'
import PasswordDialog from 'components/Profile/PasswordDialog'
import withPromise from 'helpers/withPromise'
import messages from './translate'
import './style.scss'

class UserList extends ListFromTable {
  constructor(props) {
    super(props)
    this.getTableData = props.userFetchTableData
    this.deleteEntity = props.userDelete
    this.clearReducerState = props.userClearStateList
    this.asyncData = true
    this.loadingData = true

    this.state = {
      isOpenedSetPasswordModal: false,
      activeUserID: null,
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    this.loadingData = nextProps.loader

    if (nextProps.licenseeId !== this.props.licenseeId) {
      this.reloadTableData(USER_LIST)

      return false
    }

    return super.shouldComponentUpdate(nextProps, nextState, nextContext)
  }

  onCloseSetPassword = () => {
    this.setState({
      isOpenedSetPasswordModal: false,
      activeUserID: null,
    })
  }

  onOpenSetPassword = activeUserID => {
    this.setState({
      isOpenedSetPasswordModal: true,
      activeUserID,
    })
  }

  onSetPassword = values => {
    const { activeUserID } = this.state
    const { setPassword } = this.props
    const payload = {
      ...values,
      ...activeUserID,
    }

    return withPromise(setPassword, payload).then(errors =>
      errors ? Promise.resolve(errors) : this.onCloseSetPassword(),
    )
  }

  getTableProps() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.userList),
      showFilterBar: true,
      sorting: true,
      paging: true,
      searching: true,
      creating: true,
      className: 'user-list-table',
    }
  }

  getCreateButton() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.createUser),
      link: USER_CREATE,
    }
  }

  getConfirmDialogProps() {
    const {
      intl: { formatMessage },
    } = this.props

    return {
      title: formatMessage(messages.deleteUser),
      message: formatMessage(messages.deleteUserMessage),
    }
  }

  getColumns() {
    const {
      intl: { formatMessage },
      applications,
      licensee: { usaDateFormat },
    } = this.props
    const columns = new ColumnCollection()
    const needJobsColumn = !!applications.find(
      ({ appTitle }) => appTitle === 'APP_MY_JOBS',
    )
    const dateDisplay = getDateFormat(usaDateFormat)

    columns.add(new Column('id', 'ID', { sorting: true }))
    columns.add(
      new Column('name', formatMessage(messages.name), {
        sorting: true,
        defaultSort: true,
        defaultSortOrder: Sort.ASC,
      }),
    )
    columns.add(
      new Column('email', formatMessage(messages.email), { sorting: true }),
    )
    columns.add(
      new Column('username', formatMessage(messages.username), {
        sorting: true,
      }),
    )
    columns.add(
      new Column('dateCreated', formatMessage(messages.createDate), {
        sorting: true,
        render: row => moment(row.dateCreated).format(dateDisplay),
      }),
    )
    columns.add(
      new Column('dateUpdated', formatMessage(messages.dateUpdated), {
        sorting: true,
        render: row => moment(row.dateUpdated).format(dateDisplay),
      }),
    )
    columns.add(
      new Column('roles', formatMessage(messages.roles), { sorting: true }),
    )
    columns.add(
      new Column('enabled', formatMessage(messages.active), {
        sorting: true,
        render: ({ enabled }) => (
          <FiberManualRecord
            className={cn('user-status-icon', { 'status-enabled': enabled })}
          />
        ),
      }),
    )

    if (needJobsColumn) {
      columns.add(
        new Column('canCreateJob', formatMessage(messages.canCreateJobs), {
          render: ({ canCreateJobs }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canCreateJobs,
              })}
            />
          ),
        }),
      )
      columns.add(
        new Column('canDeleteJobs', formatMessage(messages.canDeleteJobs), {
          render: ({ canDeleteJobs }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canDeleteJobs,
              })}
            />
          ),
        }),
      )
      columns.add(
        new Column('canDeleteJobSeries', formatMessage(messages.canDeleteJobSeries), {
          render: ({ canDeleteJobSeries }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canDeleteJobSeries,
              })}
            />
          ),
        }),
      )
      columns.add(
        new Column('canSeeDeletedJobs', formatMessage(messages.canSeeDeletedJobs), {
          render: ({ canSeeDeletedJobs }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canSeeDeletedJobs,
              })}
            />
          ),
        }),
      )
      columns.add(
        new Column('canRecoverDeletedJobs', formatMessage(messages.canRecoverDeletedJobs), {
          render: ({ canRecoverDeletedJobs }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canRecoverDeletedJobs,
              })}
            />
          ),
        }),
      )
    }

    columns.add(
      new Column('canReopenJobs', formatMessage(messages.canReopenJobs), {
        render: ({ canReopenJobs }) => (
          <FiberManualRecord
            className={cn('user-status-icon', {
              'status-enabled': canReopenJobs,
            })}
          />
        ),
      }),
    )

    columns.add(
      new Column(
        'canManageWorkflows',
        formatMessage(messages.canManageWorkflows),
        {
          render: ({ canManageWorkflows }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canManageWorkflows,
              })}
            />
          ),
        },
      ),
    )

    columns.add(
      new Column(
        'canSetSensorsLimits',
        formatMessage(messages.canSetSensorsLimits),
        {
          render: ({ canSetSensorsLimits }) => (
            <FiberManualRecord
              className={cn('user-status-icon', {
                'status-enabled': canSetSensorsLimits,
              })}
            />
          ),
        },
      ),
    )

    columns.add(
      new Column('userGroups', formatMessage(messages.groups), {
        sorting: true,
        render: ({ userGroups }) =>
          userGroups.map(group => group.name).join(', '),
      }),
    )

    return columns
  }

  getActions() {
    const {
      history,
      sendInvitation,
      intl: { formatMessage },
    } = this.props
    const actions = new ActionCollection()

    actions.add(
      new Action(
        'vpn_key',
        formatMessage(messages.setPassword),
        ({ id }) => {
          this.onOpenSetPassword({ id })
        },
        {
          callback: ({ email }) => ({
            hidden: !!email,
          }),
        },
      ),
    )
    actions.add(
      new Action(
        'email',
        formatMessage(messages.sendInvitation),
        ({ id }) => {
          sendInvitation({ id })
        },
        {
          callback: ({ email }) => ({
            hidden: !email,
          }),
        },
      ),
    )
    actions.add(
      new Action('edit', formatMessage(messages.editUser), ({ id }) => {
        history.push(UrlHelper.create(USER_EDIT, { id }))
      }),
    )
    actions.add(
      new Action('delete', formatMessage(messages.deleteUser), ({ id }) => {
        this.onOpenConfirmDialog({
          entityId: id,
          psfs: this.psfs,
        })
      }),
    )

    return actions
  }

  getPasswordModal = () => {
    const { isOpenedSetPasswordModal } = this.state

    return (
      <PasswordDialog
        isOpenedModal={isOpenedSetPasswordModal}
        onCloseModal={this.onCloseSetPassword}
        onSubmit={this.onSetPassword}
        needsOldPasswordField={false}
      />
    )
  }
}

UserList.propTypes = {
  status: T.bool.isRequired,
}

export default connect(
  state => ({
    loader: state.getIn(['user', 'loading']),
    tableData: state.getIn(['user', 'tableData']),
    status: state.getIn(['user', 'status']),
    licenseeId: state.getIn(['profile', 'licenseeId']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    userFetchTableData,
    userClearStateList,
    userDelete,
    sendInvitation,
    setPassword,
  },
)(injectIntl(UserList))
